/**
 * LoginForm — canonical email+password+passkey+PIN form with MFA transition.
 *
 * Composes:
 *   - <AuthMethodSelector>    — tabs between Email & Password | Passkey | [PIN]
 *   - <CaptchaField>          — from @puffin-core/captcha-ui
 *   - <MfaVerification>       — from @puffin-core/mfa-ui (when MFA required)
 *   - <PasskeyAuthFlow>       — when method === 'passkey' (requires passkey props)
 *   - <PasswordResetModal>    — when openPasswordReset fires
 *   - <ContactManagerModal>   — when openContactManager fires
 *   - pinSlot (optional)      — when supportsPIN + method === 'pin'
 *
 * App injects `auth` (from useAuth), optional passkey integration, and the
 * four REQUIRED password-reset callbacks — `usePasswordResetClient({ basePath })`
 * spread, as every host does (`web/require-login-shell-password-reset` refuses a
 * mount without them). The "Request access" modal
 * submits itself, to `accessRequestEndpoint` (default: every tenant API's
 * `/v1/auth/access-request` through the app's `/api` proxy) — see
 * `useAccessRequestSubmit` for why that is a default and not a required callback.
 *
 * Config objects are forwarded to useLoginForm (see that hook's docs for
 * rateLimitConfig / csrfConfig / subdomainConfig / credentialPersistenceConfig /
 * redirectConfig). The sign-in throttle is ON BY DEFAULT — `rateLimitConfig`
 * left undefined reaches the hook's `LOGIN_RATE_LIMIT_CONFIG` default, so a
 * bare `<LoginForm>` (planner-web's platform-admin login renders one outside
 * `<LoginShell>`) is throttled exactly like the shell. Only an explicit `null`
 * turns it off. The refusal renders through `@puffin-core/form-security`'s
 * `<RateLimitNotice>`, the one notice every throttled form in the fleet shows.
 *
 * @module @puffin-core/login-shell/components/LoginForm
 */

import { useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  CaptchaField,
  IS_CAPTCHA_CONFIGURED,
  IS_DEVELOPMENT,
  deriveCaptchaRequired,
} from '@puffin-core/captcha-ui';
import { HoneypotField } from '@puffin-core/page-shell/honeypot-field';
import { RateLimitNotice } from '@puffin-core/form-security';
import { APP_REGISTRY } from '@puffin-core/cross-app-policy';
import { AuthMethodSelector } from './AuthMethodSelector.jsx';
import { UnavailableMethodsNote } from './UnavailableMethodsNote.jsx';
import { PasskeyAuthFlow } from './PasskeyAuthFlow.jsx';
import { PasswordResetModal } from './PasswordResetModal.jsx';
import { renderSecondFactorStep } from './loginForm/secondFactorStep.jsx';
import { ContactManagerModal } from './ContactManagerModal.jsx';
import { SignInAlerts, SIGN_IN_RATE_LIMIT_ID } from './loginForm/SignInAlerts.jsx';
import { CredentialFields, fieldDescriptorShape } from './loginForm/CredentialFields.jsx';
import {
  CompanyLinks,
  ForgotPasswordPrompt,
  RememberMeField,
  SignInActions,
} from './loginForm/SignInAffordances.jsx';
import { useLoginForm } from '../hooks/useLoginForm.js';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';
import { useAccessRequestSubmit } from '../hooks/useAccessRequestSubmit.js';

/**
 * The email-and-password button's words. A surface may name its own (LOGIN-CONVERGENCE W8); otherwise a locked
 * register's form UNLOCKS, it never signs in (POS-OFFLINE-STATION-LOCK-001 U14'd-2, beside "Unlock with Passkey"),
 * and every other form keeps `<SignInActions>`'s sign-in words (`undefined` here).
 *
 * @param {{ unlockMode: boolean, submitLabel?: string, submittingLabel?: string }} signIn
 * @param {Function} translate
 * @returns {{ submitLabel: (string|undefined), submittingLabel: (string|undefined) }}
 */
function resolveSubmitLabels({ unlockMode, submitLabel, submittingLabel }, translate) {
  if (!unlockMode) return { submitLabel, submittingLabel };
  return {
    submitLabel: submitLabel ?? translate('login.unlock'),
    submittingLabel: submittingLabel ?? translate('login.unlock.loading'),
  };
}

/** No method is unavailable: a stable default, so the selector's options are not rebuilt on every render. */
const NO_UNAVAILABLE_METHODS = Object.freeze([]);

/**
 * CAPTCHA-POLICY-001 — the canonical, FAIL-CLOSED requiredness derivation
 * (`isDevelopment ? isCaptchaConfigured : true`).
 *
 * WW-ANCHOR-001 Lane 7 / Q3 (2026-07-12): this submit gate previously read
 * `IS_CAPTCHA_CONFIGURED` directly. That is the fail-OPEN shape CAPTCHA-POLICY-001
 * was created to eliminate: in a PRODUCTION build with `VITE_TURNSTILE_SITE_KEY`
 * unset, `IS_CAPTCHA_CONFIGURED` is false, so the gate evaluated false, the submit
 * button was enabled, and the login form posted with NO CAPTCHA token.
 *
 * The rule that catches this (`web/no-local-captcha-required-derivation`) shipped with
 * the apps in July and was never applied to shared packages — so it could not see the
 * one login form that every app actually renders. Same failure mode as the hardcoded
 * Monday in `@puffin-core/reporting-ui`; found by turning the rule on for packages.
 */
const IS_CAPTCHA_REQUIRED = deriveCaptchaRequired(IS_DEVELOPMENT, IS_CAPTCHA_CONFIGURED);

/**
 * The credential form. Module-private (never exported from index.js) — extracted from `LoginForm`
 * so the parent stays a thin method-switch shell rather than a ~300-line component.
 *
 * `isSubmitDisabled` is computed by the PARENT and passed in: it carries the
 * CAPTCHA-POLICY-001 fail-closed gate (see the IS_CAPTCHA_REQUIRED docblock
 * above), which must stay in exactly one place.
 *
 * LOGIN-CONVERGENCE (2026-10-11): the decoy is page-shell's `HoneypotField` (W1); the Turnstile
 * widget carries the surface's `captchaAction` (W2); "Remember me" renders only when something can
 * remember (W5); "Forgot password?" only when the surface has a reset (W8); the credential, the
 * submit label and a cancel action are the surface's to describe (W8).
 */
function EmailPasswordForm({ form, translate, t, signIn, isSubmitDisabled }) {
  const {
    unlockMode,
    showCompanyLinks,
    companyId,
    canRemember,
    hasPasswordReset,
    captchaAction,
    credentialFields,
    onCancel,
    cancelLabel,
    appKey,
    passwordResetHref,
  } = signIn;
  const { submitLabel, submittingLabel } = resolveSubmitLabels(signIn, translate);

  const showsCompanyLinks = Boolean(!unlockMode && showCompanyLinks && companyId);
  if (showsCompanyLinks) assertSignInElsewhereApp(appKey);

  return (
    // AAS-I18N-002 W8 / ruling 25: was the literal `aria-label="Login form"` —
    // English for every screen-reader user in every locale of all ten apps, and
    // invisible to every app-scoped census (RECIPE miss-class 9).
    <form onSubmit={form.handleSubmit} aria-label={translate('login.form.ariaLabel')}>
      <SignInAlerts form={form} translate={translate} />

      {/* Q-UMB-21: form-security's ONE notice, beside the submit it disables. A hand-built countdown
          inside `role="status"` made a screen reader re-announce it every second for the whole lock. */}
      {form.rateLimit && (
        <div id={SIGN_IN_RATE_LIMIT_ID}>
          <RateLimitNotice rateLimit={form.rateLimit} showRemainingAttempts t={t} />
        </div>
      )}

      <CredentialFields
        form={form}
        translate={translate}
        identifier={credentialFields?.identifier}
        secret={credentialFields?.secret}
      />

      {!unlockMode && hasPasswordReset && (
        <ForgotPasswordPrompt form={form} translate={translate} href={passwordResetHref} />
      )}

      {/* W1: the house decoy (SEC-HONEYPOT-001) — a textarea a password manager never fills. */}
      <HoneypotField ref={form.honeypotRef} name="website" />

      {!unlockMode && canRemember && <RememberMeField form={form} translate={translate} />}

      <CaptchaField
        ref={form.captchaRef}
        action={captchaAction}
        onVerify={form.handleCaptchaVerify}
        onExpire={form.handleCaptchaExpire}
        onError={form.handleCaptchaError}
      />

      <SignInActions
        form={form}
        translate={translate}
        disabled={isSubmitDisabled}
        submitLabel={submitLabel}
        submittingLabel={submittingLabel}
        onCancel={onCancel}
        cancelLabel={cancelLabel}
      />

      {showsCompanyLinks && <CompanyLinks form={form} translate={translate} />}
    </form>
  );
}

EmailPasswordForm.propTypes = {
  form: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  t: PropTypes.func,
  signIn: PropTypes.shape({
    unlockMode: PropTypes.bool,
    showCompanyLinks: PropTypes.bool,
    companyId: PropTypes.string,
    canRemember: PropTypes.bool,
    hasPasswordReset: PropTypes.bool,
    captchaAction: PropTypes.string,
    credentialFields: PropTypes.object,
    submitLabel: PropTypes.string,
    submittingLabel: PropTypes.string,
    onCancel: PropTypes.func,
    cancelLabel: PropTypes.string,
    appKey: PropTypes.string,
    passwordResetHref: PropTypes.string,
  }).isRequired,
  isSubmitDisabled: PropTypes.bool,
};

/**
 * W17 (LOGIN-CONVERGENCE, 2026-10-11): "Not your company? Sign in elsewhere" sends the person to
 * their app's TENANT-NEUTRAL apex (`resolveSignInElsewhereUrl`), so a sign-in that renders it must
 * name an app the cross-app registry knows. A sign-in that does not render it — customer mode, a
 * platform console, an unlock, no company to leave — needs no `appKey` at all. Rendering the escape
 * with no registered app is REFUSED here, loudly: it used to land on the current host's root, which
 * re-resolves the very company the person was leaving (purchase-web's tenant sign-in did exactly
 * that), and an unregistered key would have thrown on the click instead.
 *
 * @param {?string} appKey
 * @throws {TypeError} when the key is not an `APP_REGISTRY` key
 */
function assertSignInElsewhereApp(appKey) {
  if (typeof appKey === 'string' && Object.hasOwn(APP_REGISTRY, appKey)) return;
  throw new TypeError(
    `LoginForm: "Not your company? Sign in elsewhere" needs an APP_REGISTRY appKey (got ${JSON.stringify(
      appKey,
    )}). Pass the app's registry key, or turn the company links off (showCompanyLinks={false} / mode="customer").`,
  );
}

/**
 * The four reset callbacks are REQUIRED (`web/require-login-shell-password-reset`) — unless the
 * surface states it has no reset at all (`withoutPasswordReset`, W8: an access code or a counter-
 * issued PIN is not reset by email). Exported for `<LoginShell>`, which carries the same contract.
 *
 * @param {Object} props
 * @param {string} propName
 * @param {string} componentName
 * @returns {?Error}
 */
export function requiredUnlessWithoutPasswordReset(props, propName, componentName) {
  const value = props[propName];
  if (value !== undefined && typeof value !== 'function') {
    return new Error(`${componentName}: \`${propName}\` must be a function.`);
  }
  if (value === undefined && !props.withoutPasswordReset && !props.passwordResetHref) {
    return new Error(
      `${componentName}: \`${propName}\` is required unless \`withoutPasswordReset\` is set.`,
    );
  }
  return null;
}

/**
 * LOGIN-CONVERGENCE (2026-10-11): the generic seams `<LoginShell>` and `<LoginForm>` both take.
 * Exported for `<LoginShell>` so the two declarations cannot drift.
 */
export const SIGN_IN_SEAM_PROP_TYPES = Object.freeze({
  // W2: Turnstile's `action` — what the token is for; a server that binds one refuses any other.
  captchaAction: PropTypes.string,
  // W8: the credential the person signs in with (`CredentialFields`).
  credentialFields: PropTypes.shape({
    identifier: fieldDescriptorShape,
    secret: fieldDescriptorShape,
  }),
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
  withoutPasswordReset: PropTypes.bool,
  // W20: "Forgot password?" as a link to the surface's own reset PAGE (no dialog, no callbacks).
  passwordResetHref: PropTypes.string,
  // W7 + W3: the surface's own words for its server's codes, and which of them prove a wrong secret.
  refusalMessages: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ message: PropTypes.string.isRequired, locked: PropTypes.bool }),
    ]),
  ),
  credentialRefusalCodes: PropTypes.arrayOf(PropTypes.string),
  // W4: the surface's own sentence for a blank form.
  credentialsRequiredMessage: PropTypes.string,
  // W6: open the reset on arrival; hear a finished reset.
  passwordResetInitiallyOpen: PropTypes.bool,
  onPasswordResetSuccess: PropTypes.func,
});

export function LoginForm({
  auth,
  // Q-L16-98 · F-98-16 / Q-ARG-1: the brand lockup the enrolment screen names ("Your
  // ParcelPuffin account needs…"). `<LoginShell>` forwards its own `appName`.
  appName,
  companyId,
  companyName,
  onSuccess,
  // AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29): forwarded straight through to `useLoginForm`,
  // which calls it when the server answers `requiresAuthenticator`. The SHELL owns the screen
  // swap; this component only carries the seam.
  onAuthenticatorChallenge,
  supportsPasskey = true,
  supportsPIN = false,
  pinSlot,
  passkeyProps,
  onPasswordResetInitiate,
  onPasswordResetVerify,
  onPasswordResetComplete,
  onPasswordResetResend,
  accessRequestEndpoint,
  showCompanyLinks = true,
  rateLimitConfig,
  csrfConfig,
  subdomainConfig,
  credentialPersistenceConfig,
  redirectConfig,
  onAuthEvent,
  appKey,
  passwordResetCaptchaSlot = null,
  unlockMode = false,
  // LOGIN-CONVERGENCE (2026-10-11) — the generic seams; each is documented in the README.
  captchaAction,
  credentialFields,
  submitLabel,
  submittingLabel,
  onCancel,
  cancelLabel,
  withoutPasswordReset = false,
  passwordResetHref,
  refusalMessages,
  credentialRefusalCodes,
  credentialsRequiredMessage,
  passwordResetInitiallyOpen = false,
  onPasswordResetSuccess,
  unavailableMethods = NO_UNAVAILABLE_METHODS,
  t,
}) {
  // Declared ABOVE useLoginForm so the hook groups read computed → custom
  // (component-state-order). Order-safe: the factory closes over PROPS only —
  // it never reads the form hook's output, so there is nothing to TDZ on.
  const methods = useMemo(() => {
    const list = [];
    if (supportsPIN) list.push('pin');
    list.push('email_password');
    if (supportsPasskey) list.push('passkey');
    return list;
  }, [supportsPasskey, supportsPIN]);
  const unavailableIds = useMemo(
    () => unavailableMethods.map(({ method }) => method),
    [unavailableMethods],
  );
  const unavailableHintId = useId();

  const form = useLoginForm({
    auth,
    onSuccess,
    onAuthenticatorChallenge,
    rateLimitConfig,
    csrfConfig,
    subdomainConfig,
    credentialPersistenceConfig,
    redirectConfig,
    onAuthEvent,
    appKey,
    hasIdentifier: credentialFields?.identifier !== null,
    credentialRules: credentialFields,
    lockedIdentifier: credentialFields?.identifier?.readOnly
      ? (credentialFields.identifier.value ?? null)
      : null,
    credentialsRequiredMessage,
    refusalMessages,
    credentialRefusalCodes,
    passwordResetInitiallyOpen: passwordResetInitiallyOpen && !withoutPasswordReset,
    onPasswordResetSuccess,
    captchaRequired: IS_CAPTCHA_REQUIRED,
  });
  const submitAccessRequest = useAccessRequestSubmit(accessRequestEndpoint);
  const translate = useLoginTranslate(t);

  const showMethodSelector = methods.length > 1;

  // The second-factor screens — enrolment hand-off, enrolment, verification — REPLACE the form.
  const secondFactorStep = renderSecondFactorStep({
    form,
    auth,
    appName,
    redirectConfig,
    translate,
    t,
  });
  if (secondFactorStep) return secondFactorStep;

  const isSubmitDisabled =
    form.isLoading ||
    (IS_CAPTCHA_REQUIRED && !form.captchaToken) ||
    Boolean(form.rateLimit?.isLimited);

  const methodSelectorSlot = showMethodSelector && (
    <div className="flex justify-center">
      <AuthMethodSelector
        selectedMethod={form.selectedAuthMethod}
        onMethodChange={form.setSelectedAuthMethod}
        methods={methods}
        passkeySupported={passkeyProps?.passkeySupported}
        passkeyStatusLoading={passkeyProps?.passkeyStatusLoading}
        platformAuthenticatorAvailable={passkeyProps?.platformAuthAvailable}
        unavailableMethods={unavailableIds}
        unavailableHintId={unavailableHintId}
        t={t}
      />
    </div>
  );

  return (
    <>
      {methodSelectorSlot}
      <UnavailableMethodsNote unavailableMethods={unavailableMethods} t={t} />

      {supportsPIN && form.selectedAuthMethod === 'pin' && pinSlot}

      {supportsPasskey && form.selectedAuthMethod === 'passkey' && passkeyProps && (
        <PasskeyAuthFlow
          onPasskeyLogin={passkeyProps.onPasskeyLogin}
          onAuthenticate={passkeyProps.onAuthenticate}
          onFallback={
            passkeyProps.onFallback || (() => form.setSelectedAuthMethod('email_password'))
          }
          subdomain={passkeyProps.subdomain}
          passkeySupported={passkeyProps.passkeySupported}
          platformAuthAvailable={passkeyProps.platformAuthAvailable}
          loading={passkeyProps.loading}
          error={passkeyProps.error}
          clearError={passkeyProps.clearError}
          unlockMode={unlockMode}
          t={t}
        />
      )}

      {form.selectedAuthMethod === 'email_password' && (
        <EmailPasswordForm
          form={form}
          translate={translate}
          t={t}
          signIn={{
            unlockMode,
            showCompanyLinks,
            companyId,
            canRemember: typeof credentialPersistenceConfig?.save === 'function',
            hasPasswordReset: !withoutPasswordReset,
            captchaAction,
            credentialFields,
            submitLabel,
            submittingLabel,
            onCancel,
            cancelLabel,
            appKey,
            passwordResetHref,
          }}
          isSubmitDisabled={isSubmitDisabled}
        />
      )}

      {!withoutPasswordReset && !passwordResetHref && (
        <PasswordResetModal
          isOpen={form.passwordResetOpen}
          onClose={form.closePasswordReset}
          initialEmail={form.email}
          initialSubdomain={form.subdomain}
          onInitiate={onPasswordResetInitiate}
          onVerify={onPasswordResetVerify}
          onComplete={onPasswordResetComplete}
          onResend={onPasswordResetResend}
          onSuccess={form.handlePasswordResetSuccess}
          captchaSlot={passwordResetCaptchaSlot}
          t={t}
        />
      )}

      <ContactManagerModal
        isOpen={form.contactManagerOpen}
        onClose={form.closeContactManager}
        companyName={companyName}
        subdomain={form.subdomain}
        onSubmit={submitAccessRequest}
        t={t}
      />
    </>
  );
}

LoginForm.propTypes = {
  auth: PropTypes.shape({
    login: PropTypes.func.isRequired,
    verifyMfa: PropTypes.func,
    cancelMfa: PropTypes.func,
    clearError: PropTypes.func,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    mfaRequired: PropTypes.bool,
    mfaMethods: PropTypes.arrayOf(PropTypes.string),
    // The first-factor enrolment leg (Q-L16-98 · F-98-16 platform; Q-ARG-1 tenant) — present in both modes.
    mfaEnrollmentRequired: PropTypes.bool,
    mfaEnrollment: PropTypes.object,
    beginMfaEnrollment: PropTypes.func,
    completeMfaEnrollment: PropTypes.func,
    finishMfaEnrollment: PropTypes.func,
    cancelMfaEnrollment: PropTypes.func,
    preferredMethod: PropTypes.string,
    maskedPhone: PropTypes.string,
    maskedEmail: PropTypes.string,
  }).isRequired,
  appName: PropTypes.string,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  onSuccess: PropTypes.func,
  onAuthenticatorChallenge: PropTypes.func,
  supportsPasskey: PropTypes.bool,
  supportsPIN: PropTypes.bool,
  pinSlot: PropTypes.node,
  passkeyProps: PropTypes.object,
  onPasswordResetInitiate: requiredUnlessWithoutPasswordReset,
  onPasswordResetVerify: requiredUnlessWithoutPasswordReset,
  onPasswordResetComplete: requiredUnlessWithoutPasswordReset,
  onPasswordResetResend: requiredUnlessWithoutPasswordReset,
  accessRequestEndpoint: PropTypes.string,
  showCompanyLinks: PropTypes.bool,
  rateLimitConfig: PropTypes.object,
  csrfConfig: PropTypes.object,
  subdomainConfig: PropTypes.object,
  credentialPersistenceConfig: PropTypes.object,
  redirectConfig: PropTypes.object,
  onAuthEvent: PropTypes.func,
  // Registry app key (e.g. 'planner') — powers the "Sign in elsewhere"
  // tenant-neutral apex redirect (Login-1).
  appKey: PropTypes.string,
  passwordResetCaptchaSlot: PropTypes.node,
  unlockMode: PropTypes.bool,
  ...SIGN_IN_SEAM_PROP_TYPES,
  /**
   * Methods offered but unavailable now, each with its reason (already translated): shown as unavailable, never hidden
   * (POS-OFFLINE-STATION-LOCK-001 U14'd-2).
   */
  unavailableMethods: PropTypes.arrayOf(
    PropTypes.shape({ method: PropTypes.string.isRequired, reason: PropTypes.string.isRequired }),
  ),
  t: PropTypes.func,
};

export default LoginForm;
